import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EventPageType } from '@graphql/generated-base';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import type { MinimalPage } from '@shared/hooks/useDefaultPage';
import { useDefaultPage } from '@shared/hooks/useDefaultPage';
import { createContext } from '@shared/utils/createContext';

/**
 * Key identifying the page whose visibility the caller is asking about.
 * Always carries `pageSlug` because several page types don't have slugs that
 * match their `EventPageType` string (e.g., App pages with `accommodations`
 * slug, migrated custom pages like `weddingparty`). Matching on slug rather
 * than type is the authoritative check.
 */
export type PageContentKey = Readonly<{ type: EventPageType; pageSlug: string }>;

type ShouldRevealPageContent = (key: PageContentKey) => boolean;

type PageContentVisibilityContextValue = Readonly<{
  shouldReveal: ShouldRevealPageContent;
  markTargetLoaded: () => void;
  /**
   * Resolved slug of the deep-linked target — URL slug when explicit, else
   * the default page's slug (welcome or first visible page) for root routes.
   * Consumed by `useIsActiveTarget` so per-page content hooks can prioritize
   * batching / fire the loaded signal even on root routes without welcome.
   */
  targetSlug: string | undefined;
}>;

// Fallback: reveal all page chrome after this long even if the target page's
// content query hasn't reported loaded. Tuned to feel snappy on slow networks
// while still giving fast connections time to paint real content.
export const REVEAL_ALL_TIMEOUT_MS = 3000;

// Reserved slug for the welcome page when it's been migrated to a custom page
// via the `useMigratedCustomPageFeatureFlags` flag. Matches the constant in
// `useWelcomePage`.
const WELCOME_PAGE_SLUG = 'welcome';

const [ContextProvider, useContextValue] = createContext<PageContentVisibilityContextValue>({
  name: 'PageContentVisibility',
  // Default so consumers outside the provider don't crash (e.g., admin preview
  // iframes, tests not exercising the provider). shouldReveal → true means
  // those contexts behave as if everything is always revealed.
  initialValue: { shouldReveal: () => true, markTargetLoaded: () => {}, targetSlug: undefined },
});

/**
 * Provides `shouldRevealPageContent(key)` which returns `true` for the
 * deep-linked target page initially, then for every page once EITHER:
 *   - the target page's content query reports loaded (via `useSignalPageLoaded`)
 *   - a REVEAL_ALL_TIMEOUT_MS fallback timer expires
 *
 * Only stacked layouts (Aloha, Brannan single-page) render non-target pages
 * pre-hydration, so deferring chrome for them until the target is painted
 * keeps the initial focus on where the user actually is.
 *
 * Mirrors the "initial target" semantics of `useIsCurrentPageSsrFetchEnabled`
 * in the per-page content hooks, so widget prioritization (batchMode: 'off')
 * and chrome visibility share the same source of truth.
 *
 * See doc/improve-guest-site-perf/15-ssr-narrow-surface/PLAN.md.
 */
export const PageContentVisibilityProvider: React.FC<
  React.PropsWithChildren<{
    eventHandle: string;
    /**
     * Event pages, used to resolve the default page when the URL is the root
     * route (`/<handle>`). Without this, a root-route event that defaults to
     * a non-welcome page (no welcome page on the site) would leave chrome
     * hidden on the actually-rendered landing page.
     */
    pages?: ReadonlyArray<MinimalPage>;
    /**
     * When true, `allRevealed` starts as `true` and the fallback timer is
     * never scheduled — every `shouldReveal(key)` immediately returns true.
     * Use for layouts that don't benefit from deferring chrome (Brannan classic
     * only renders one page at a time, so there's nothing stacked to hide;
     * Brannan single-page has page photos that already fill the viewport).
     * Aloha is the only layout that needs deferral.
     * @default false
     */
    isInitiallyRevealed?: boolean;
  }>
> = ({ eventHandle, pages, isInitiallyRevealed = false, children }) => {
  const { currentPageSlug } = useCurrentPageSlug(eventHandle);
  const { defaultPage } = useDefaultPage(pages);

  // Capture the initial (deep-linked) slug once. URL changes driven by
  // `usePageInScreenWhenScrolling` must NOT shift what "target" means.
  const initialSlugRef = useRef(currentPageSlug);
  const initialSlug = initialSlugRef.current;

  // If the initial slug isn't a real page (e.g., `/handle/old-page` typo,
  // which `useScrollToPageWhenUrlChanges` will redirect to the default),
  // ignore it and fall back to the default page. Otherwise `targetSlug`
  // would stay pinned to the invalid slug forever, leaving chrome hidden
  // on the actually-rendered page until the 3s fallback timer.
  //
  // Trust the initial slug until `pages` arrives — during the load gap we
  // don't yet know what's valid. Once `pages` resolves, re-evaluate.
  const isInitialSlugValid = !!initialSlug && (!pages || pages.some(p => p.pageSlug === initialSlug));

  // On root routes, `initialSlug` is empty — the landing page is whichever
  // `useDefaultPage` resolves to (welcome if present, else first page). This
  // also handles the invalid-slug case above.
  const targetSlug = (isInitialSlugValid ? initialSlug : undefined) || defaultPage?.pageSlug;

  const [allRevealed, setAllRevealed] = useState(isInitiallyRevealed);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const markTargetLoaded = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setAllRevealed(true);
  }, []);

  // Fallback timer — covers slow networks and routes where the "target" has no
  // content query (e.g., welcome page's data comes from EventWebsiteGuest).
  // Skipped entirely when `isInitiallyRevealed` short-circuits the deferral.
  //
  // `isInitiallyRevealed` can also flip at runtime (e.g., the provider is
  // mounted before `layout` is resolved, then `layout` loads as Brannan
  // classic). Flip `allRevealed` in response so we don't hold stale state.
  useEffect(() => {
    if (isInitiallyRevealed) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setAllRevealed(true);
      return;
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setAllRevealed(true);
    }, REVEAL_ALL_TIMEOUT_MS);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isInitiallyRevealed]);

  const shouldReveal = useCallback<ShouldRevealPageContent>(
    key => {
      if (allRevealed) return true;

      if (!targetSlug) {
        // No resolved target (pages haven't loaded yet, or caller didn't pass
        // pages on a root route). Conservative fallback: reveal only welcome
        // keys so root-route events with a welcome page still light up.
        return key.type === EventPageType.welcome || (key.type === EventPageType.custom && key.pageSlug === WELCOME_PAGE_SLUG);
      }

      // Match on actual pageSlug, which is authoritative across all page
      // types (native, migrated-custom, type/slug-mismatched like App pages).
      return key.pageSlug === targetSlug;
    },
    [allRevealed, targetSlug],
  );

  const value = useMemo<PageContentVisibilityContextValue>(() => ({ shouldReveal, markTargetLoaded, targetSlug }), [shouldReveal, markTargetLoaded, targetSlug]);

  return <ContextProvider value={value}>{children}</ContextProvider>;
};

PageContentVisibilityProvider.displayName = 'PageContentVisibilityProvider';

export const useShouldRevealPageContent = () => useContextValue().shouldReveal;

/**
 * Returns true when this page's slug matches the deep-linked target — whether
 * the URL had an explicit slug OR the root route resolved its default page.
 *
 * Use in per-page content hooks to gate `batchMode: 'off'` priority and to
 * drive `useSignalPageLoaded`'s isTarget argument. Without this, events
 * landing on root without a welcome page would never signal loaded (the
 * URL-based `useIsCurrentPageSsrFetchEnabled` doesn't see the default).
 */
export const useIsActiveTarget = (slugs: string | ReadonlyArray<string>): boolean => {
  const { targetSlug } = useContextValue();
  if (!targetSlug) return false;
  return typeof slugs === 'string' ? slugs === targetSlug : slugs.includes(targetSlug);
};

/**
 * Per-page-content hooks call this to tell the provider "I'm the target and
 * I've finished loading" — flipping `allRevealed` so every page's chrome
 * fades in at once. No-op when `isTargetPage` is false; idempotent after the
 * first successful signal.
 */
export const useSignalPageLoaded = (isTargetPage: boolean, isLoaded: boolean) => {
  const { markTargetLoaded } = useContextValue();
  useEffect(() => {
    if (isTargetPage && isLoaded) markTargetLoaded();
  }, [isTargetPage, isLoaded, markTargetLoaded]);
};
